import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isIOS } from '../services/appVersion.mjs';
import { sipService } from '../services/sip';
import { serverHostname } from '../services/serverBase.mjs';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { AudioTunnel, sasToEmoji } from '../services/tunnel';
import { FONT, colors, radii, shadows } from '../theme';
import { Shield, ShieldCheck, ShieldAlert, Lock, Phone, Mic, MicOff, Volume2, Volume1, Minimize2, X, Check } from 'lucide-react';

const CALL_STATE = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RINGING: 'ringing',
  VERIFYING: 'verifying',
  // SEC-05: the peer presented a key this person never confirmed (or none
  // where one was expected). Keys are derived, the SAS exists, and NO media
  // flows until the человек says the code matched (call-lifecycle §held_untrusted).
  HELD: 'held',
  ACTIVE: 'active',
  ENDED: 'ended',
};

export default function CallRoom({ linkCode, targetExtension, incoming, roomJoin, iceServers, iceTransportPolicy, tunnelMode, roomNumber, directCallId, minimized, onMinimize, onClose, onPeerJoined, nativeDisplayMode, nativeSAS, nativeElapsed, nativeMuted }) {
  const { user } = useAuth();
  const [callState, setCallState] = useState(CALL_STATE.CONNECTING);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [callUuid, setCallUuid] = useState(null);
  const [error, setError] = useState('');
  const [encryption, setEncryption] = useState(null);
  const [verifySteps, setVerifySteps] = useState([]);
  const [speaker, setSpeaker] = useState(false);
  const [sasEmoji, setSasEmoji] = useState(null);
  const [heldReason, setHeldReason] = useState(null);
  const [sasVerified, setSasVerified] = useState(null); // null=pending, true=both confirmed, false=rejected
  const [authState, setAuthState] = useState(null); // ADR-014 identity-key verdict
  const [peerSasVerified, setPeerSasVerified] = useState(null); // null=pending, true/false
  const [mySasChoice, setMySasChoice] = useState(null); // null=not chosen, true/false
  const audioRef = useRef();
  const remoteStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const timerRef = useRef();
  const startTimeRef = useRef();
  const mountedRef = useRef(true);
  const sessionRef = useRef(null);
  const endedRef = useRef(false);
  const tunnelRef = useRef(null);
  const nativeCallActiveRef = useRef(false);
  const useNativeAudioRef = useRef(false);  // true when foreground native tunnel is active (iOS)
  const useNativeSIPRef = useRef(false);    // true when native SIP (LinPhone) is active (iOS)
  const nativeRingTimeoutRef = useRef(null);
  const isNative = typeof window !== 'undefined' && !!window.VoxioxNative;

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Native iOS bridge: call lifecycle & callbacks
  useEffect(() => {
    if (!isNative) return;
    // In native display mode, DON'T call callStarted — native tunnel is already active.
    // On iOS tunnel mode, DON'T call callStarted — foreground native tunnel manages audio session.
    // On iOS SIP mode, DON'T call callStarted — LinPhone manages its own audio session.
    // On Android, callStarted IS needed for tunnel calls (audio focus, proximity, wake lock).
    // callStarted is needed for SIP/WebRTC calls on all platforms (except iOS native SIP).
    const isIOSNativeTunnel = tunnelMode
      && isIOS()
      && typeof window.VoxioxNative?.startForegroundTunnel === 'function';
    const isIOSNativeSIP = !tunnelMode
      && isIOS()
      && typeof window.VoxioxNative?.makeSIPCall === 'function';
    let callStartedTimer = null;
    if (!nativeDisplayMode && !isIOSNativeTunnel && !isIOSNativeSIP) {
      // Delay callStarted by 500ms — gives iOS time to set _nativeCallActive flag
      // via evaluateJavaScript in applicationWillEnterForeground before we proceed.
      // Without this delay, WKWebView unfreeze → useEffect fires → callStarted runs
      // BEFORE the flag arrives from native.
      callStartedTimer = setTimeout(() => {
        if (window._nativeCallActive || !mountedRef.current) {
          console.log('[E2EE-DIAG] callStarted: SKIPPED — native call active or unmounted');
          return;
        }
        // Call without arguments — Android @JavascriptInterface doesn't accept params.
        // iOS bridge defaults tunnelMode to false when opts is undefined.
        window.VoxioxNative.callStarted();
        nativeCallActiveRef.current = true;
      }, 500);
    }
    window.VoxioxBridge = window.VoxioxBridge || {};
    window.VoxioxBridge.onProximityChange = (isNear) => {
      setSpeaker(!isNear);
    };
    window.VoxioxBridge.onAudioRouteChange = (route) => {
      setSpeaker(route === 'speaker');
    };
    // Listen for mute changes from CallKit (native mode)
    window.VoxioxBridge.onNativeMuteChange = (m) => {
      setMuted(m);
    };
    return () => {
      if (callStartedTimer) clearTimeout(callStartedTimer);
      // If foreground native tunnel is active and endCall wasn't called yet,
      // end the tunnel now to avoid a dangling native connection.
      if (useNativeAudioRef.current && !endedRef.current && window.VoxioxNative?.endForegroundTunnel) {
        console.log('[E2EE-DIAG] Component unmounting with active native tunnel — ending it');
        window.VoxioxNative.endForegroundTunnel();
        useNativeAudioRef.current = false;
      }
      // If native SIP call is active and endCall wasn't called, end it
      if (useNativeSIPRef.current && !endedRef.current && window.VoxioxNative?.endSIPCall) {
        console.log('[SIP-Native] Component unmounting with active native SIP call — ending it');
        window.VoxioxNative.endSIPCall();
        useNativeSIPRef.current = false;
      }
      // Don't call callEnded if foreground native tunnel or native SIP was used (they manage own cleanup)
      if (nativeCallActiveRef.current && !useNativeAudioRef.current && !useNativeSIPRef.current) {
        window.VoxioxNative.callEnded();
        nativeCallActiveRef.current = false;
      }
      if (window.VoxioxBridge) {
        delete window.VoxioxBridge.onProximityChange;
        delete window.VoxioxBridge.onAudioRouteChange;
        delete window.VoxioxBridge.onNativeMuteChange;
      }
    };
  }, []); // eslint-disable-line

  // ─── Native display mode: show active call UI without creating tunnel ───
  useEffect(() => {
    if (!nativeDisplayMode) return;
    // Parse SAS from hex string (e.g. "269806d5" → [0x26, 0x98, 0x06, 0xd5])
    if (nativeSAS) {
      const sasBytes = [];
      for (let i = 0; i < nativeSAS.length; i += 2) {
        sasBytes.push(parseInt(nativeSAS.substr(i, 2), 16));
      }
      setSasEmoji(sasBytes);
    }
    setEncryption({ verified: true, fingerprint: null });
    setCallState(CALL_STATE.ACTIVE);
    // Set initial mute state from native
    if (nativeMuted) setMuted(true);
    // Start timer from elapsed time
    startTimeRef.current = Date.now() - (nativeElapsed || 0) * 1000;
    timerRef.current = setInterval(() => {
      if (mountedRef.current) {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);
    // Wire peer SAS verify callback from native tunnel
    window.VoxioxBridge = window.VoxioxBridge || {};
    window.VoxioxBridge.onNativePeerSasVerify = (verified) => {
      console.log('[E2EE-DIAG] Peer SAS verify from native tunnel:', verified);
      setPeerSasVerified(verified);
    };
    return () => {
      clearInterval(timerRef.current);
      if (window.VoxioxBridge) {
        delete window.VoxioxBridge.onNativePeerSasVerify;
      }
    };
  }, []); // eslint-disable-line

  // ─── SAS verification via tunnel ───────────────────────────
  // Compute overall SAS verification status
  useEffect(() => {
    if (mySasChoice === false || peerSasVerified === false) {
      setSasVerified(false); // One side rejected
    } else if (mySasChoice === true && peerSasVerified === true) {
      setSasVerified(true); // Both confirmed
    }
  }, [mySasChoice, peerSasVerified]);

  const handleSasConfirm = () => {
    setMySasChoice(true);
    // The emoji matched, so this peer's identity key is trusted from now on and
    // a later key change becomes a loud warning instead of a routine prompt.
    tunnelRef.current?.confirmPeerIdentity?.();
    if ((nativeDisplayMode || useNativeAudioRef.current) && window.VoxioxNative?.sendNativeSasVerify) {
      window.VoxioxNative.sendNativeSasVerify(true);
    } else if (tunnelRef.current) {
      tunnelRef.current.sendSasVerify(true);
    }
  };

  const handleSasDeny = () => {
    setMySasChoice(false);
    if ((nativeDisplayMode || useNativeAudioRef.current) && window.VoxioxNative?.sendNativeSasVerify) {
      window.VoxioxNative.sendNativeSasVerify(false);
    } else if (tunnelRef.current) {
      tunnelRef.current.sendSasVerify(false);
    }
  };

  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      if (mountedRef.current) {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
  }, []);

  const endCall = useCallback(async (status = 'ended') => {
    if (endedRef.current) return; // Prevent double-end race condition
    endedRef.current = true;
    console.log('[E2EE-DIAG] endCall called, status:', status, 'useNativeAudio:', useNativeAudioRef.current, 'useNativeSIP:', useNativeSIPRef.current, 'stack:', new Error().stack?.split('\n').slice(1, 4).join(' | '));
    stopTimer();

    // Clear native ring timeout if active
    if (nativeRingTimeoutRef.current) {
      clearTimeout(nativeRingTimeoutRef.current);
      nativeRingTimeoutRef.current = null;
    }

    // Foreground native tunnel: end via native bridge (no JS tunnel/AudioContext to clean up)
    if (useNativeAudioRef.current && window.VoxioxNative?.endForegroundTunnel) {
      console.log('[E2EE-DIAG] Calling endForegroundTunnel from JS endCall, status:', status);
      window.VoxioxNative.endForegroundTunnel();
      useNativeAudioRef.current = false;
      if (mountedRef.current) setCallState(CALL_STATE.ENDED);
      if (callUuid) {
        try { await api.updateCallStatus(callUuid, status); } catch (e) {}
      }
      setTimeout(onClose, 1500);
      return;
    }

    // Native SIP (LinPhone): end via native bridge
    if (useNativeSIPRef.current && window.VoxioxNative?.endSIPCall) {
      console.log('[SIP-Native] Calling endSIPCall from JS endCall, status:', status);
      window.VoxioxNative.endSIPCall();
      useNativeSIPRef.current = false;
      if (mountedRef.current) setCallState(CALL_STATE.ENDED);
      if (callUuid) {
        try { await api.updateCallStatus(callUuid, status); } catch (e) {}
      }
      setTimeout(onClose, 1500);
      return;
    }

    // Terminate JS tunnel if active
    if (tunnelRef.current) {
      try { tunnelRef.current.leave(); } catch (e) {}
      tunnelRef.current = null;
    }
    // Terminate only OUR session, not a session from another CallRoom instance
    if (sessionRef.current) {
      try { sessionRef.current.terminate(); } catch (e) {}
      sessionRef.current = null;
    }
    // Cleanup AudioContext
    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.disconnect(); } catch (e) {}
      sourceNodeRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (e) {}
      audioContextRef.current = null;
    }
    // Native iOS: end call
    if (nativeDisplayMode && window.VoxioxNative?.endNativeCall) {
      // Native display mode: end the native tunnel (not callEnded which is blocked)
      window.VoxioxNative.endNativeCall();
    } else {
      if (nativeCallActiveRef.current && window.VoxioxNative) {
        window.VoxioxNative.callEnded();
        nativeCallActiveRef.current = false;
      }
      // Don't kill CallKit if native E2EE call is active — we're a rogue JS CallRoom
      // that shouldn't exist (race condition), and killing CallKit would end the native tunnel.
      if (window.VoxioxNative?.endCallKit && !window._nativeCallActive) {
        window.VoxioxNative.endCallKit();
      }
    }
    if (mountedRef.current) setCallState(CALL_STATE.ENDED);
    if (callUuid) {
      try { await api.updateCallStatus(callUuid, status); } catch (e) {}
    }
    setTimeout(onClose, 1500);
  }, [callUuid, onClose, stopTimer]);

  // ─── Global hook for CallKit / native end call ──────────────
  useEffect(() => {
    // Expose endCall globally so CallKit "End" button and Dashboard can terminate the active call
    window._endActiveCall = () => endCall('ended');
    return () => { delete window._endActiveCall; };
  }, [endCall]);

  // ─── Encryption verification ────────────────────────────────
  const verifyEncryption = useCallback(async () => {
    if (!mountedRef.current) return;
    setCallState(CALL_STATE.VERIFYING);

    const steps = [
      { label: 'Установка DTLS соединения...', done: false },
      { label: 'Проверка сертификата...', done: false },
      { label: 'Шифрование SRTP активировано', done: false },
    ];
    setVerifySteps([...steps]);

    await delay(600);
    steps[0].done = true;
    if (!mountedRef.current) return;
    setVerifySteps([...steps]);

    await delay(500);
    let fingerprint = null;
    try {
      const pc = sipService.getConnection();
      if (pc) {
        const stats = await pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'certificate' && report.fingerprint) {
            fingerprint = report.fingerprint;
          }
        });
      }
    } catch (e) {}

    steps[1].done = true;
    if (!mountedRef.current) return;
    setVerifySteps([...steps]);

    await delay(400);
    steps[2].done = true;
    if (!mountedRef.current) return;
    setVerifySteps([...steps]);
    setEncryption({ verified: true, fingerprint });

    await delay(300);
    if (mountedRef.current) setCallState(CALL_STATE.ACTIVE);
  }, []);

  useEffect(() => {
    // Native display mode: don't init any call — just display the UI.
    // Audio is handled by the native tunnel, not by JS.
    if (nativeDisplayMode) return;

    let tunnelDelayTimer = null;

    if (tunnelMode && roomNumber) {
      if (window.VoxioxNative) {
        // On iOS: delay tunnel init by 500ms to give native time to set
        // _nativeCallActive flag via evaluateJavaScript. Without this delay,
        // WKWebView unfreezes → buffered WS fires → CallRoom mounts → initTunnelCall
        // creates a JS tunnel BEFORE _nativeCallActive arrives from native.
        tunnelDelayTimer = setTimeout(() => {
          if (window._nativeCallActive || !mountedRef.current) {
            console.log('[E2EE-DIAG] initTunnelCall: SKIPPED after delay — native call active');
            return; // Don't close CallRoom — onNativeCallDisplay may update it
          }
          initTunnelCall();
        }, 500);
      } else {
        initTunnelCall();
      }
    } else if (incoming) {
      answerIncoming();
    } else {
      initOutgoingCall();
    }
    return () => {
      console.log('[E2EE-DIAG] CallRoom cleanup: useNativeAudio=', useNativeAudioRef.current, 'useNativeSIP=', useNativeSIPRef.current, 'ended=', endedRef.current);
      if (tunnelDelayTimer) clearTimeout(tunnelDelayTimer);
      stopTimer();
      // Clean up native ring timeout
      if (nativeRingTimeoutRef.current) {
        clearTimeout(nativeRingTimeoutRef.current);
        nativeRingTimeoutRef.current = null;
      }
      // Clean up foreground native tunnel callbacks
      if (useNativeAudioRef.current && window.VoxioxBridge) {
        console.log('[E2EE-DIAG] CallRoom unmounting while native tunnel active — deleting callbacks but NOT ending tunnel');
        delete window.VoxioxBridge.onForegroundTunnelPeerJoined;
        delete window.VoxioxBridge.onForegroundTunnelPeerLeft;
        delete window.VoxioxBridge.onForegroundTunnelSasVerify;
        delete window.VoxioxBridge.onForegroundTunnelError;
      }
      // Clean up native SIP callbacks
      if (useNativeSIPRef.current && window.VoxioxBridge) {
        console.log('[SIP-Native] CallRoom unmounting — deleting SIP callbacks');
        delete window.VoxioxBridge.onNativeSIPCallState;
      }
      if (tunnelRef.current) {
        if (tunnelRef.current._ringTimeout) clearTimeout(tunnelRef.current._ringTimeout);
        try { tunnelRef.current.leave(); } catch (e) {}
        tunnelRef.current = null;
      }
      if (sessionRef.current) {
        try { sessionRef.current.terminate(); } catch (e) {}
        sessionRef.current = null;
      }
    };
  }, []); // eslint-disable-line

  async function answerIncoming() {
    try {
      // ═══════ iOS: route incoming SIP through native LinPhone ═══════
      // LinPhone stores incoming INVITE as pendingIncomingCall (does NOT auto-answer).
      // When CallRoom mounts and user tapped "Answer", we tell LinPhone to answer now.
      // LinPhone answers → Asterisk bridges caller↔LinPhone, CANCELs JsSIP leg.
      // Audio flows through native process → earpiece/speaker control works.
      const canUseNativeSIP = isNative
        && isIOS()
        && typeof window.VoxioxNative?.makeSIPCall === 'function'
        && !tunnelMode;

      if (canUseNativeSIP) {
        console.log('[SIP-Native] Using LinPhone for incoming SIP call');
        useNativeSIPRef.current = true;

        // Wire native callbacks → React state
        window.VoxioxBridge = window.VoxioxBridge || {};

        window.VoxioxBridge.onNativeSIPCallState = (state, msg) => {
          if (!mountedRef.current) return;
          console.log('[SIP-Native] Incoming call state:', state, msg || '');
          switch (state) {
            case 'connecting':
              setCallState(CALL_STATE.CONNECTING);
              break;
            case 'ringing':
              setCallState(CALL_STATE.RINGING);
              break;
            case 'active':
              startTimer();
              // Show SRTP encryption badge
              setEncryption({ verified: true, fingerprint: null });
              setCallState(CALL_STATE.ACTIVE);
              break;
            case 'ended':
              endCall('ended');
              break;
            case 'error':
              setError(msg || 'Ошибка SIP вызова');
              endCall('failed');
              break;
            default:
              break;
          }
        };

        if (mountedRef.current) setCallState(CALL_STATE.CONNECTING);

        // Tell LinPhone to answer the pending incoming call.
        // checkSIPCallState will find the pending call and answer it,
        // or report current state if already active (fallback).
        if (window.VoxioxNative?.answerSIPCall) {
          console.log('[SIP-Native] Telling LinPhone to answer pending incoming call');
          window.VoxioxNative.answerSIPCall();
        } else if (window.VoxioxNative?.checkSIPCallState) {
          window.VoxioxNative.checkSIPCallState();
        }
        return; // Skip JsSIP answer
      }

      // ═══════ Non-iOS: use JsSIP/WebRTC (unchanged) ═══════
      if (mountedRef.current) setCallState(CALL_STATE.RINGING);

      sipService.answer({
        iceServers,
        iceTransportPolicy,
        onStream: (remoteStream) => {
          if (!mountedRef.current) return;
          remoteStreamRef.current = remoteStream;
          startTimer();
          if (audioRef.current) {
            audioRef.current.srcObject = remoteStream;
            audioRef.current.play().catch(() => {});
          }
          verifyEncryption();
        },
        onEnded: () => endCall('ended'),
        onFailed: (e) => {
          if (mountedRef.current) {
            setError('Ошибка звонка');
          }
          endCall('failed');
        },
      });
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
        setCallState(CALL_STATE.ENDED);
      }
      setTimeout(onClose, 2000);
    }
  }

  async function initTunnelCall() {
    try {
      // Extra guard: if native call became active after CallRoom mounted (race condition),
      // don't create a JS tunnel — it would kill the native call.
      // DON'T close CallRoom — onNativeCallDisplay may fire from didBecomeActive
      // and will update this CallRoom to nativeDisplayMode.
      if (window._nativeCallActive) {
        console.log('[E2EE-DIAG] initTunnelCall: BLOCKED — native call active, waiting for display update');
        return;
      }
      console.log('[E2EE-DIAG] initTunnelCall:', { tunnelMode, roomNumber, directCallId, linkCode, targetExtension, isNative });
      if (mountedRef.current) setCallState(CALL_STATE.CONNECTING);

      // A direct call is recorded by the server, which is the only party that
      // sees the whole call: accept, decline, timeout, cancel, the peer
      // leaving. Both clients used to log their own on mount, so every call
      // left two rows — the second one claiming you had called yourself, with
      // no callee, stuck at "ringing" forever because nothing here ever
      // reported an answer.
      //
      // Link calls still log from here: there is no directCallId for the
      // server to key the history on.
      let loggedCallUuid = null;
      if (!directCallId) {
        try {
          const { call_uuid } = await api.startCall(linkCode || null, targetExtension);
          loggedCallUuid = call_uuid;
          if (mountedRef.current) setCallUuid(call_uuid);
        } catch (e) { console.warn('[Call] Failed to log call start:', e); }
      }

      // ═══════ iOS: route through native audio pipeline ═══════
      // WKWebView's WebContent process manages its own AVAudioSession (WebKit Bug 167788).
      // overrideOutputAudioPort has NO effect on WKWebView audio → always stuck on speaker.
      // Solution: bypass WKWebView audio entirely, use NativeTunnelClient + NativeAudioEngine.
      // IMPORTANT: Only iOS has startForegroundTunnel. Android also has window.VoxioxNative
      // but does NOT have this function — Android must use JS AudioTunnel.
      const canUseNativeTunnel = isNative
        && isIOS()
        && typeof window.VoxioxNative?.startForegroundTunnel === 'function';
      if (canUseNativeTunnel) {
        console.log('[E2EE-DIAG] Using NATIVE foreground tunnel (bypassing WKWebView audio)');
        useNativeAudioRef.current = true;

        // Wire native callbacks → React state
        window.VoxioxBridge = window.VoxioxBridge || {};

        window.VoxioxBridge.onForegroundTunnelPeerJoined = (sasHex) => {
          console.log('[E2EE-DIAG] Native tunnel peer joined, SAS:', sasHex, 'mounted:', mountedRef.current, 'ended:', endedRef.current);
          if (!mountedRef.current) return;
          if (nativeRingTimeoutRef.current) {
            clearTimeout(nativeRingTimeoutRef.current);
            nativeRingTimeoutRef.current = null;
          }
          startTimer();
          // Parse SAS hex → byte array for emoji display
          const sasBytes = [];
          for (let i = 0; i < sasHex.length; i += 2) {
            sasBytes.push(parseInt(sasHex.substr(i, 2), 16));
          }
          setSasEmoji(sasBytes);
          setEncryption({ verified: true, fingerprint: null });
          setCallState(CALL_STATE.ACTIVE);
        };

        window.VoxioxBridge.onForegroundTunnelPeerLeft = () => {
          console.log('[E2EE-DIAG] Native tunnel peer left — mounted:', mountedRef.current, 'ended:', endedRef.current);
          if (!mountedRef.current) return;
          endCall('ended');
        };

        // SEC-05: the native tunnel derived keys but withheld media — the
        // peer's identity is unconfirmed and the person decides here.
        window.VoxioxBridge.onForegroundTunnelHeld = (state, sasHex) => {
          if (!mountedRef.current) return;
          console.log('[E2EE-DIAG] native tunnel HELD —', state);
          if (nativeRingTimeoutRef.current) {
            clearTimeout(nativeRingTimeoutRef.current);
            nativeRingTimeoutRef.current = null;
          }
          const sasBytes = [];
          for (let i = 0; i < sasHex.length; i += 2) sasBytes.push(parseInt(sasHex.substr(i, 2), 16));
          setSasEmoji(sasBytes);
          setHeldReason(state === 'anonymous' ? 'anonymous' : 'new-device');
          setCallState(CALL_STATE.HELD);
        };

        // Re-key after the peer reconnected, or handed off between stacks on
        // its phone: new DH, new SAS, the previous comparison is void — the
        // same rule the JS tunnel applies in onRekeyed below.
        window.VoxioxBridge.onForegroundTunnelRekeyed = (sasHex) => {
          if (!mountedRef.current) return;
          console.log('[E2EE-DIAG] Native tunnel re-keyed — SAS changed');
          const sasBytes = [];
          for (let i = 0; i < sasHex.length; i += 2) sasBytes.push(parseInt(sasHex.substr(i, 2), 16));
          setSasEmoji(sasBytes);
          setPeerSasVerified(null);
        };
        window.VoxioxBridge.onForegroundTunnelReconnecting = () => {
          console.log('[E2EE-DIAG] Native tunnel: peer reconnecting');
        };

        window.VoxioxBridge.onForegroundTunnelSasVerify = (verified) => {
          if (!mountedRef.current) return;
          console.log('[E2EE-DIAG] Native tunnel peer SAS verify:', verified);
          setPeerSasVerified(verified);
        };

        window.VoxioxBridge.onForegroundTunnelError = (msg) => {
          console.log('[E2EE-DIAG] Native tunnel error:', msg, 'mounted:', mountedRef.current, 'ended:', endedRef.current);
          if (!mountedRef.current) return;
          setError(msg);
          endCall('failed');
        };

        // Start native tunnel
        window.VoxioxNative.startForegroundTunnel(
          roomNumber,
          loggedCallUuid || '',
          targetExtension || '',
          directCallId || null
        );

        if (mountedRef.current) setCallState(CALL_STATE.RINGING);

        // Ring timeout
        nativeRingTimeoutRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          console.log('[E2EE-DIAG] Native tunnel ringing timeout (45s)');
          setError('Абонент не ответил');
          endCall('timeout');
        }, 45000);

        return; // Skip JS AudioTunnel creation
      }

      // ═══════ Non-iOS: use JS AudioTunnel (unchanged) ═══════
      const tunnel = new AudioTunnel();
      tunnelRef.current = tunnel;

      tunnel.onAuthState = (state) => setAuthState(state);
      tunnel.onHeld = (state, sasBytes) => {
        if (!mountedRef.current) return;
        console.log('[E2EE-DIAG] media withheld — peer identity is', state, '(SEC-05)');
        if (tunnel._ringTimeout) { clearTimeout(tunnel._ringTimeout); tunnel._ringTimeout = null; }
        setSasEmoji(sasBytes ? Array.from(sasBytes) : null);
        setHeldReason(state);
        setCallState(CALL_STATE.HELD);
      };
      tunnel.onPeerJoined = (sasBytes) => {
        if (!mountedRef.current) return;
        console.log('[E2EE-DIAG] Peer joined! E2EE established');
        // Tell the dashboard the call really happened, so closing it does not
        // send a cancel for a conversation that took place.
        onPeerJoined?.();
        if (tunnel._ringTimeout) { clearTimeout(tunnel._ringTimeout); tunnel._ringTimeout = null; }
        startTimer();
        // E2EE established — show SAS verification
        setSasEmoji(sasBytes ? Array.from(sasBytes) : null);
        setEncryption({ verified: true, fingerprint: null });
        setCallState(CALL_STATE.ACTIVE);
      };

      tunnel.onSasVerify = (msg) => {
        if (!mountedRef.current) return;
        setPeerSasVerified(msg.verified);
      };

      tunnel.onPeerLeft = () => {
        if (!mountedRef.current) return;
        console.log('[E2EE-DIAG] Peer left, ending call');
        endCall('ended');
      };

      tunnel.onPeerReconnecting = () => {
        console.log('[E2EE-DIAG] Connection lost — reconnecting');
      };

      tunnel.onRekeyed = (sasBytes) => {
        if (!mountedRef.current) return;
        console.log('[E2EE-DIAG] Re-keyed after reconnect — SAS changed');
        // New DH after reconnect — SAS changed, previous verification is void
        setSasEmoji(sasBytes ? Array.from(sasBytes) : null);
        setPeerSasVerified(null);
      };

      tunnel.onError = (msg) => {
        if (!mountedRef.current) return;
        console.log('[E2EE-DIAG] Tunnel error:', msg);
        setError(msg);
        endCall('failed');
      };

      // Direct E2EE call uses directCallId for authorization, link-based uses room ownership
      if (directCallId) {
        console.log('[E2EE-DIAG] Joining as direct, room:', roomNumber, 'callId:', directCallId);
        tunnel.joinAsDirect(roomNumber, directCallId);
      } else {
        console.log('[E2EE-DIAG] Joining as creator, room:', roomNumber);
        tunnel.joinAsCreator(roomNumber);
      }
      if (mountedRef.current) setCallState(CALL_STATE.RINGING);

      // Timeout: if no peer joins within 45s, give up
      tunnel._ringTimeout = setTimeout(() => {
        if (!mountedRef.current) return;
        // If peer already joined, _ringTimeout was cleared
        console.log('[E2EE-DIAG] Ringing timeout (45s), no peer joined');
        setError('Абонент не ответил');
        endCall('timeout');
      }, 45000);
    } catch (err) {
      if (mountedRef.current) {
        console.log('[E2EE-DIAG] initTunnelCall error:', err.message);
        setError(err.message);
        setCallState(CALL_STATE.ENDED);
      }
      setTimeout(onClose, 2000);
    }
  }

  async function initOutgoingCall() {
    let uuid = null;
    try {
      // See initTunnelCall: direct calls are recorded server-side.
      if (!directCallId) {
        try {
          const { call_uuid } = await api.startCall(linkCode || null, targetExtension);
          uuid = call_uuid;
          if (mountedRef.current) setCallUuid(call_uuid);
        } catch (e) { console.warn('[Call] Failed to log call start:', e); }
      }

      // ═══════ iOS: route SIP through native LinPhone ═══════
      // WKWebView's WebContent process manages its own AVAudioSession (WebKit Bug 167788).
      // overrideOutputAudioPort has NO effect on WKWebView audio → always stuck on speaker.
      // Solution: LinPhone SDK manages SIP + audio natively.
      const canUseNativeSIP = isNative
        && isIOS()
        && typeof window.VoxioxNative?.makeSIPCall === 'function'
        && !tunnelMode;

      if (canUseNativeSIP) {
        console.log('[SIP-Native] Using LinPhone for native SIP call (bypassing WKWebView audio)');
        useNativeSIPRef.current = true;

        // Wire native callbacks → React state
        window.VoxioxBridge = window.VoxioxBridge || {};

        window.VoxioxBridge.onNativeSIPCallState = (state, msg) => {
          if (!mountedRef.current) return;
          console.log('[SIP-Native] Call state:', state, msg || '');
          switch (state) {
            case 'connecting':
              setCallState(CALL_STATE.CONNECTING);
              break;
            case 'ringing':
              setCallState(CALL_STATE.RINGING);
              break;
            case 'active':
              startTimer();
              if (uuid) api.updateCallStatus(uuid, 'answered').catch(() => {});
              // Show SRTP encryption badge (LinPhone uses SDES-SRTP)
              setEncryption({ verified: true, fingerprint: null });
              setCallState(CALL_STATE.ACTIVE);
              break;
            case 'ended':
              endCall('ended');
              break;
            case 'error':
              setError(msg || 'Ошибка SIP вызова');
              endCall('failed');
              break;
          }
        };

        // Make call via native LinPhone
        window.VoxioxNative.makeSIPCall(targetExtension);
        if (mountedRef.current) setCallState(CALL_STATE.CONNECTING);
        return; // Skip JsSIP
      }

      // ═══════ Non-iOS or fallback: use JsSIP/WebRTC (unchanged) ═══════
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (mountedRef.current) setCallState(CALL_STATE.RINGING);

      const domain = process.env.REACT_APP_SIP_DOMAIN || serverHostname();
      console.log('[CALL] Placing call to', targetExtension, 'domain:', domain, 'iceServers:', iceServers?.length || 0);

      // Guard: don't place call if component already unmounted
      if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }

      const session = sipService.call({
        extension: targetExtension,
        domain,
        localStream: stream,
        iceServers,
        iceTransportPolicy,
        onStream: (remoteStream) => {
          if (!mountedRef.current) return;
          remoteStreamRef.current = remoteStream;
          console.log('[AUDIO] Creator got remote stream, tracks:', remoteStream.getAudioTracks().length);
          startTimer();
          if (audioRef.current) {
            audioRef.current.srcObject = remoteStream;
            audioRef.current.play()
              .then(() => console.log('[AUDIO] Creator play() OK'))
              .catch((e) => console.error('[AUDIO] Creator play() failed:', e));
          }
          if (uuid) api.updateCallStatus(uuid, 'answered').catch(() => {});
          verifyEncryption();
        },
        onEnded: () => endCall('ended'),
        onFailed: (e) => {
          if (!mountedRef.current) return;
          setError(e?.cause === 'Rejected' ? 'Вызов отклонён' : 'Не удалось дозвониться');
          endCall('failed');
        }
      });
      sessionRef.current = session;
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
        setCallState(CALL_STATE.ENDED);
      }
      if (uuid) api.updateCallStatus(uuid, 'failed').catch(() => {});
      setTimeout(onClose, 2000);
    }
  }

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if ((nativeDisplayMode || useNativeAudioRef.current) && window.VoxioxNative?.setNativeMute) {
      // Native tunnel (foreground or display mode): mute/unmute native tunnel mic
      window.VoxioxNative.setNativeMute(next);
    } else if (useNativeSIPRef.current && window.VoxioxNative?.setSIPMute) {
      // Native SIP (LinPhone): mute/unmute via LinPhone
      window.VoxioxNative.setSIPMute(next);
    } else if (tunnelRef.current) {
      tunnelRef.current.setMuted(next);
    } else {
      sipService.mute(next);
    }
  };

  const toggleSpeaker = useCallback(async () => {
    const next = !speaker;
    setSpeaker(next);

    // Native iOS: use native audio routing
    if (isNative) {
      // Native SIP (LinPhone): use LinPhone audio device API for better control
      if (useNativeSIPRef.current && window.VoxioxNative?.setSIPSpeaker) {
        window.VoxioxNative.setSIPSpeaker(next);
      } else {
        window.VoxioxNative.setSpeaker(next);
      }
      return;
    }

    const stream = remoteStreamRef.current;
    if (!stream) return;

    if (next) {
      // === ГРОМКАЯ СВЯЗЬ ===
      // Стратегия 1 (Chrome/Android): setSinkId
      if (audioRef.current?.setSinkId) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const speakerDev = devices.find(d => d.kind === 'audiooutput' && /speaker/i.test(d.label));
          if (speakerDev) {
            await audioRef.current.setSinkId(speakerDev.deviceId);
            console.log('[SPEAKER] setSinkId OK:', speakerDev.label);
            return;
          }
        } catch (e) { console.warn('[SPEAKER] setSinkId failed:', e); }
      }
      // Стратегия 2 (fallback): AudioContext — переключает audio session на playback (динамик)
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') await ctx.resume();
        if (sourceNodeRef.current) {
          try { sourceNodeRef.current.disconnect(); } catch (e) {}
        }
        sourceNodeRef.current = ctx.createMediaStreamSource(stream);
        sourceNodeRef.current.connect(ctx.destination);
        if (audioRef.current) audioRef.current.volume = 0;
        console.log('[SPEAKER] AudioContext route OK');
      } catch (e) {
        console.warn('[SPEAKER] AudioContext fallback failed:', e);
      }
    } else {
      // === ТЕЛЕФОННЫЙ ДИНАМИК ===
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.disconnect(); } catch (e) {}
        sourceNodeRef.current = null;
      }
      if (audioRef.current?.setSinkId) {
        try { await audioRef.current.setSinkId(''); } catch (e) {}
      }
      if (audioRef.current) {
        audioRef.current.volume = 1.0;
        audioRef.current.srcObject = stream;
        audioRef.current.play().catch(() => {});
      }
      console.log('[SPEAKER] Earpiece mode restored');
    }
  }, [speaker, isNative]);

  const fmtDuration = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  // The native side needs "connected", not "the call screen is open": the
  // screen-off proximity lock must not blank the display while an outgoing
  // call is still ringing. Signal draws the same line (LockManager: proximity
  // only in IN_CALL, never while dialling or hands-free).
  useEffect(() => {
    if (callState !== CALL_STATE.ACTIVE) return;
    try { window.VoxioxNative?.callConnected?.(); } catch (e) { /* web */ }
  }, [callState]);


  const isVerifying = callState === CALL_STATE.VERIFYING;
  const isActive = callState === CALL_STATE.ACTIVE;

  const stateLabel = {
    [CALL_STATE.CONNECTING]: 'Подключение...',
    [CALL_STATE.RINGING]: 'Вызов...',
    [CALL_STATE.VERIFYING]: 'Проверка шифрования...',
    [CALL_STATE.HELD]: 'Личность не подтверждена',
    [CALL_STATE.ACTIVE]: fmtDuration(duration),
    [CALL_STATE.ENDED]: 'Звонок завершён',
  }[callState] || '';

  // When minimized, render only audio element to keep call alive
  if (minimized) {
    return (
      <audio ref={audioRef} autoPlay playsInline />
    );
  }

  return (
    <div style={styles.overlay}>
      <audio ref={audioRef} autoPlay playsInline />
      <div style={styles.room}>
        <div style={styles.pulse}>
          <div style={{
            ...styles.ring,
            ...(isVerifying ? styles.ringVerifying : {}),
            ...(isActive ? styles.ringActive : {}),
          }} />
          <div style={styles.avatar}>
            {isVerifying ? <Shield size={40} color={colors.gray900} strokeWidth={1.5} /> : isActive && encryption?.verified ? <Lock size={40} color={colors.green} strokeWidth={1.5} /> : <Phone size={40} color={colors.gray400} strokeWidth={1.5} />}
          </div>
        </div>

        <div style={styles.extension}>{targetExtension}</div>
        <div style={{
          ...styles.status,
          color: isActive ? colors.green : isVerifying ? colors.gray900 : colors.gray400,
        }}>
          {stateLabel}
        </div>

        {/* Encryption verification steps */}
        {isVerifying && (
          <div style={styles.verifyBox}>
            {verifySteps.map((step, i) => (
              <div key={i} style={styles.verifyStep}>
                <span style={{ color: step.done ? colors.green : colors.gray400, marginRight: 8 }}>
                  {step.done ? <Check size={14} /> : '○'}
                </span>
                <span style={{ color: step.done ? colors.gray900 : colors.gray400, fontSize: 13 }}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* SEC-05: held call — the person decides before a word is exchanged */}
        {callState === CALL_STATE.HELD && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 20px', maxWidth: 420 }}>
            <ShieldAlert size={28} color={'#e8a13c'} />
            <div style={{ fontSize: 15, fontWeight: 600, color: colors.gray900, textAlign: 'center' }}>
              {heldReason === 'anonymous'
                ? 'Собеседник не предъявил личность'
                : 'Ключ собеседника изменился'}
            </div>
            <div style={{ fontSize: 13, color: colors.gray400, textAlign: 'center', lineHeight: 1.45 }}>
              Звук не передаётся. Сверьте код с собеседником по другому каналу
              и продолжайте только при полном совпадении.
            </div>
            {sasEmoji && (
              <div style={{ fontSize: 30, letterSpacing: 6 }}>{(sasToEmoji(sasEmoji) || []).join(' ')}</div>
            )}
            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
              <button
                onClick={() => {
                  // Whichever pipeline held the call is the one to release it.
                  if (window.VoxioxNative?.confirmTunnelPeer && useNativeAudioRef.current) {
                    window.VoxioxNative.confirmTunnelPeer();
                  } else {
                    tunnelRef.current?.confirmHeldPeer?.();
                  }
                }}
                style={{ padding: '10px 22px', borderRadius: 999, border: 'none', background: colors.green, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Код совпал — продолжить
              </button>
              <button
                onClick={() => endCall('rejected_identity')}
                style={{ padding: '10px 22px', borderRadius: 999, border: 'none', background: colors.red, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Завершить
              </button>
            </div>
          </div>
        )}

        {/* Active call — encryption badge */}
        {isActive && encryption?.verified && !tunnelMode && (
          <div style={styles.encBadge}>
            {/* SEC-06: a SIP call is encrypted TO the server, which decrypts,
                mixes and re-encrypts the audio. Calling that end-to-end was
                the lie the audit named — two differently trusted calls looked
                identical on this screen. */}
            <span style={{ color: '#e8a13c', fontSize: 13, display: 'inline-flex', alignItems: 'center' }}>
              <ShieldAlert size={14} color={'#e8a13c'} style={{marginRight:4}} /> Совместимый звонок — сервер участвует в передаче звука
            </span>
            {encryption.fingerprint && (
              <div style={styles.fingerprint}>
                SHA-256: {encryption.fingerprint.slice(0, 23)}...
              </div>
            )}
          </div>
        )}

        {/* E2EE SAS interactive verification */}
        {isActive && (tunnelMode || nativeDisplayMode) && sasEmoji && (
          <SASVerification
            sasEmoji={sasEmoji}
            sasVerified={sasVerified}
            authState={authState}
            mySasChoice={mySasChoice}
            peerSasVerified={peerSasVerified}
            onConfirm={handleSasConfirm}
            onDeny={handleSasDeny}
          />
        )}

        {error && <div style={styles.error}>{error}</div>}

        {isActive && (
          <div style={styles.controls}>
            <button onClick={toggleMute} style={{ ...styles.ctrl, ...(muted ? styles.ctrlActive : {}) }}>
              {muted ? <MicOff size={24} /> : <Mic size={24} />}
              <span style={styles.ctrlLabel}>{muted ? 'Вкл.' : 'Выкл.'}</span>
            </button>
            <button onClick={toggleSpeaker} style={{ ...styles.ctrl, ...(speaker ? styles.ctrlActive : {}) }}>
              {speaker ? <Volume2 size={24} /> : <Volume1 size={24} />}
              <span style={styles.ctrlLabel}>Динамик</span>
            </button>
            {onMinimize && (
              <button onClick={onMinimize} style={styles.minimizeBtn}>
                <Minimize2 size={24} />
                <span style={styles.ctrlLabel}>Свернуть</span>
              </button>
            )}
            <button onClick={() => endCall('ended')} style={styles.hangup}>
              <X size={24} />
              <span style={styles.ctrlLabel}>Завершить</span>
            </button>
          </div>
        )}

        {callState === CALL_STATE.RINGING && (
          <button onClick={() => endCall('missed')} style={styles.cancelBtn}>
            Отмена
          </button>
        )}
      </div>
      <style>{`
        @keyframes pulse { 0%,100% { transform:scale(1); opacity:.3 } 50% { transform:scale(1.1); opacity:.7 } }
        @keyframes glow { 0%,100% { box-shadow: 0 0 8px rgba(21,20,26,0.3) } 50% { box-shadow: 0 0 20px rgba(21,20,26,0.6) } }
        @keyframes pulseGreen { 0%,100% { box-shadow: 0 0 8px rgba(22,163,74,0.2) } 50% { box-shadow: 0 0 16px rgba(22,163,74,0.5) } }
      `}</style>
    </div>
  );
}



// ADR-014: what the identity-key check concluded about the peer.
const AUTH_COPY = {
  verified:     { text: 'Собеседник подтверждён ранее', tone: 'ok' },
  pinned:       { text: 'Ключ собеседника известен', tone: 'ok' },
  'tofu-new':   { text: 'Первый звонок с этим собеседником — сверьте эмодзи', tone: 'neutral' },
  'new-device': { text: 'Новое устройство собеседника — обязательно сверьте эмодзи', tone: 'warn' },
  anonymous:    { text: 'Собеседник не аутентифицирован (гость)', tone: 'neutral' },
};

function AuthBadge({ authState }) {
  const copy = AUTH_COPY[authState];
  if (!copy) return null;
  const color = copy.tone === 'ok' ? colors.green : copy.tone === 'warn' ? colors.amber : colors.gray500;
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
      color, marginBottom: 10, lineHeight: 1.4,
    }}>
      {copy.text}
    </div>
  );
}

function SASVerification({ sasEmoji, sasVerified, mySasChoice, peerSasVerified, authState, onConfirm, onDeny }) {
  const emojis = sasToEmoji(sasEmoji) || [];

  // Determine background and status
  const isRejected = sasVerified === false;
  const isBothConfirmed = sasVerified === true;
  const isPending = mySasChoice === null;
  const isWaitingPeer = mySasChoice === true && peerSasVerified === null;

  const bgColor = isRejected ? 'rgba(221,122,92,0.06)' : isBothConfirmed ? 'rgba(79,182,140,0.06)' : 'transparent';
  const borderColor = isRejected ? 'rgba(221,122,92,0.3)' : isBothConfirmed ? 'rgba(79,182,140,0.3)' : colors.gray200;

  return (
    <div style={{
      background: bgColor, border: `1px solid ${borderColor}`,
      borderRadius: radii.lg, padding: '16px 20px', maxWidth: 300,
      textAlign: 'center', transition: 'all 0.3s',
    }}>
      {/* Status icon */}
      <div style={{ marginBottom: 8 }}>
        {isRejected ? (
          <ShieldAlert size={28} color={colors.red} />
        ) : isBothConfirmed ? (
          <ShieldCheck size={28} color={colors.green} />
        ) : (
          <Shield size={28} color={colors.gray400} />
        )}
      </div>

      <AuthBadge authState={authState} />

      {/* SAS emojis */}
      <div style={{ fontSize: 36, letterSpacing: 8, marginBottom: 12, lineHeight: 1.4 }}>
        {emojis.join(' ')}
      </div>

      {/* Instructions / status */}
      {isRejected && (
        <div style={{ fontSize: 13, color: colors.red, fontWeight: 600, marginBottom: 8 }}>
          ⚠ Иконки не совпали. Возможна MITM-атака.
        </div>
      )}
      {isBothConfirmed && (
        <div style={{ fontSize: 13, color: colors.green, fontWeight: 600, marginBottom: 8 }}>
          ✓ Соединение верифицировано
        </div>
      )}
      {isPending && (
        <div style={{ fontSize: 12, color: colors.gray500, marginBottom: 12, lineHeight: 1.5 }}>
          Собеседник вас слышит. Сверьте иконки по голосу и подтвердите, что они совпадают.
        </div>
      )}
      {isWaitingPeer && (
        <div style={{ fontSize: 12, color: colors.gray500, marginBottom: 8 }}>
          Ожидание подтверждения собеседника...
        </div>
      )}

      {/* Buttons */}
      {isPending && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button onClick={onConfirm} style={{
            flex: 1, background: colors.greenDot, border: 'none', color: colors.gray50,
            padding: '10px 16px', borderRadius: radii.sm, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: FONT,
          }}>
            ✓ Совпадают
          </button>
          <button onClick={onDeny} style={{
            flex: 1, background: colors.gray100, border: `1px solid ${colors.gray200}`,
            color: colors.gray600, padding: '10px 16px', borderRadius: radii.sm,
            fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: FONT,
          }}>
            ✗ Не совпадают
          </button>
        </div>
      )}
    </div>
  );
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 100,
    background: 'rgba(249,250,251,0.95)',
    backdropFilter: 'blur(20px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: FONT,
  },
  room: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 },
  pulse: { position: 'relative', width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  ring: {
    position: 'absolute', width: '100%', height: '100%', borderRadius: '50%',
    border: `2px solid ${colors.gray200}`, transition: 'all 0.5s',
  },
  ringVerifying: { animation: 'glow 1.5s infinite', borderColor: colors.gray900 },
  ringActive: { animation: 'pulseGreen 2s infinite', borderColor: colors.green },
  avatar: { zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  extension: { fontSize: 30, fontWeight: 600, color: colors.gray900, fontFamily: "'New York', 'Iowan Old Style', 'Palatino', Georgia, serif" },
  status: { fontSize: 15, fontWeight: 500 },
  verifyBox: {
    background: colors.gray100, borderRadius: radii.md, padding: '12px 16px',
    minWidth: 240, display: 'flex', flexDirection: 'column', gap: 6,
  },
  verifyStep: { display: 'flex', alignItems: 'center' },
  encBadge: {
    background: colors.greenLight, border: `1px solid ${colors.greenBorder}`,
    borderRadius: radii.md, padding: '8px 14px', textAlign: 'center',
  },
  fingerprint: {
    fontSize: 10, color: colors.gray500, fontFamily: 'monospace',
    marginTop: 4, letterSpacing: 0.5,
  },
  error: { color: colors.red, fontSize: 13, background: colors.redLight, padding: '8px 16px', borderRadius: radii.sm },
  controls: { display: 'flex', gap: 16, marginTop: 16 },
  ctrl: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    background: colors.gray100, border: 'none',
    borderRadius: radii.lg, width: 72, height: 72, cursor: 'pointer',
    color: colors.gray600, fontSize: 24, justifyContent: 'center',
  },
  ctrlActive: { background: colors.gray900, color: colors.white },
  ctrlLabel: { fontSize: 10, fontWeight: 500 },
  hangup: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    background: colors.red, border: 'none',
    borderRadius: radii.lg, width: 72, height: 72, cursor: 'pointer',
    color: colors.white, fontSize: 24, justifyContent: 'center',
  },
  minimizeBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    background: colors.gray100, border: 'none',
    borderRadius: radii.lg, width: 72, height: 72, cursor: 'pointer',
    color: colors.gray600, fontSize: 24, justifyContent: 'center',
  },
  cancelBtn: {
    background: colors.red, border: 'none',
    color: colors.white, padding: '12px 32px', borderRadius: radii.lg,
    fontSize: 15, fontWeight: 500, cursor: 'pointer', marginTop: 16,
    fontFamily: 'inherit',
  },
};
